import { fetchAuthSession } from 'aws-amplify/auth';
import { API_ENDPOINT } from '../aws-config';

const getAuthToken = async (): Promise<string> => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || '';
  } catch (error) {
    console.error('Error getting auth token:', error);
    return '';
  }
};

const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = await getAuthToken();

  const response = await fetch(`${API_ENDPOINT}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `API Error: ${response.status}`);
  }

  return response.json();
};

// Hospital API
export const hospitalApi = {
  getAll: async () => {
    try {
      const result = await apiCall('/hospitals');
      return Array.isArray(result) ? result : (result.hospitals || []);
    } catch (error) {
      console.error('Error fetching hospitals:', error);
      return [];
    }
  },

  get: async (id: string) => {
    return await apiCall(`/hospitals/${id}`);
  },

  create: async (hospital: {
    name: string;
    address?: string;
    contactEmail?: string;
    contactPhone?: string;
    subscriptionAmount: number;
    currency?: string;
  }) => {
    return await apiCall('/hospitals', { method: 'POST', body: JSON.stringify(hospital) });
  },

  update: async (id: string, data: {
    name?: string;
    address?: string;
    contactEmail?: string;
    contactPhone?: string;
    subscriptionAmount?: number;
    currency?: string;
    isActive?: boolean;
    hasDevice?: boolean;
    deviceDescription?: string;
    deviceContractAmount?: number;
    deviceMonthlyPayment?: number;
    deviceCurrency?: string;
    paymentStatusOverride?: 'paid' | 'partial' | 'pending' | 'overdue' | null;
  }) => {
    return await apiCall(`/hospitals/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  delete: async (id: string) => {
    return await apiCall(`/hospitals/${id}`, { method: 'DELETE' });
  },
};

// Payment API
export const paymentApi = {
  getAll: async () => {
    try {
      const result = await apiCall('/payments');
      return Array.isArray(result) ? result : (result.payments || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      return [];
    }
  },

  getByHospital: async (hospitalId: string) => {
    try {
      const result = await apiCall(`/payments/hospital/${hospitalId}`);
      return Array.isArray(result) ? result : (result.payments || []);
    } catch (error) {
      console.error('Error fetching payments for hospital:', error);
      return [];
    }
  },

  create: async (payment: {
    hospitalId: string;
    amount: number;
    month: string;
    paymentDate: string;
    notes?: string;
    idempotencyKey?: string;
  }) => {
    return await apiCall('/payments', { method: 'POST', body: JSON.stringify(payment) });
  },

  delete: async (paymentId: string) => {
    return await apiCall(`/payments/${paymentId}`, { method: 'DELETE' });
  },
};

// Stats API
export const statsApi = {
  getDashboard: async () => {
    try {
      return await apiCall('/stats/dashboard');
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return { totalAllTime: 0, totalCurrentYear: 0, totalCurrentMonth: 0 };
    }
  },
};

// User API
export const userApi = {
  getAll: async () => {
    try {
      const result = await apiCall('/users');
      return result.users || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  },

  create: async (user: { email: string; name?: string }) => {
    return await apiCall('/users', { method: 'POST', body: JSON.stringify(user) });
  },

  delete: async (username: string) => {
    return await apiCall(`/users/${encodeURIComponent(username)}`, { method: 'DELETE' });
  },
};

// Contract API
export const contractApi = {
  getUploadUrl: async (params: {
    hospitalId: string;
    hospitalName: string;
    contractType: 'subscription' | 'device' | 'other';
    fileName: string;
    contentType: string;
  }) => {
    return await apiCall('/contracts/upload', { method: 'POST', body: JSON.stringify(params) });
  },

  listByHospital: async (hospitalId: string, hospitalName?: string) => {
    try {
      const qs = hospitalName ? `?hospitalName=${encodeURIComponent(hospitalName)}` : '';
      return await apiCall(`/contracts/hospital/${hospitalId}${qs}`);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      return { contracts: [] };
    }
  },

  getDownloadUrl: async (s3Key: string) => {
    return await apiCall('/contracts/download', { method: 'POST', body: JSON.stringify({ s3Key }) });
  },

  delete: async (s3Key: string) => {
    return await apiCall('/contracts/delete', { method: 'POST', body: JSON.stringify({ s3Key }) });
  },
};

export default { hospitalApi, paymentApi, statsApi, userApi, contractApi };
